import { create } from "zustand";
import api, { CartItem, getPriceForSize } from "../lib/api";
import { useAuthStore } from "./auth";

interface CartStore {
  items: CartItem[];
  open: boolean;
  loading: boolean;
  fetch: () => Promise<void>;
  add: (productId: string, quantity?: number, size?: string | null) => Promise<void>;
  update: (id: string, quantity: number) => Promise<void>;
  remove: (id: string) => Promise<void>;
  clear: () => Promise<void>;
  toggleOpen: () => void;
}

export const useCartStore = create<CartStore>((set, get) => ({
  items: [],
  open: false,
  loading: false,

  fetch: async () => {
    if (!useAuthStore.getState().token) return;
    set({ loading: true });
    const { data } = await api.get<CartItem[]>("/cart");
    set({ items: data, loading: false });
  },

  add: async (productId, quantity = 1, size = null) => {
    await api.post("/cart", { productId, quantity, size });
    await get().fetch();
  },

  update: async (id, quantity) => {
    await api.put(`/cart/${id}`, { quantity });
    await get().fetch();
  },

  remove: async (id) => {
    await api.delete(`/cart/${id}`);
    await get().fetch();
  },

  clear: async () => {
    await api.delete("/cart");
    set({ items: [] });
  },

  toggleOpen: () => set((s) => ({ open: !s.open })),
}));

export function cartTotal(items: CartItem[], currency = "USD") {
  return items.reduce((sum, item) => {
    if (!item.product) return sum;
    return sum + getPriceForSize(item.product, item.size, currency) * item.quantity;
  }, 0);
}
